import isNil from 'lodash/isNil';
import { expVal } from '@atlassian/jira-feature-experiments';
import { isSubtaskHierarchyLevel } from '@atlassian/jira-issue-type-hierarchies/src/index.tsx';
import type {
	StatisticField,
	ExtraField as IssueExtraField,
	Issue,
} from '@atlassian/jira-software-backlog-uif-types/src/index.tsx';
import { NUMERIC_ESTIMATE_FIELD_ID_PREFIX, ORIGINAL_ESTIMATE_ID } from '../../constants.tsx';
import {
	type ExtraField,
	type ExtraFieldDate,
	type ExtraFieldHtml,
	type ExtraFieldText,
	isExtraFieldDate,
	isExtraFieldHtml,
	isExtraFieldText,
} from '../../extra-fields/types.tsx';
import type { Card, CardEstimate, CardPriority, Person } from '../../types.tsx';
import { hasEpicField, isEpicField } from '../types.tsx';

const isNumericFieldEstimate = (statisticField: StatisticField): boolean =>
	statisticField && statisticField?.statFieldId?.includes(NUMERIC_ESTIMATE_FIELD_ID_PREFIX);

const isTimeOriginalEstimate = (statisticField: StatisticField): boolean =>
	statisticField && statisticField?.statFieldId?.startsWith(ORIGINAL_ESTIMATE_ID);

const transformEstimateStatistic = ({
	estimateStatistic,
	trackingStatistic,
}: Issue): CardEstimate => {
	const timeRemainingEstimate = trackingStatistic?.statFieldValue
		? {
				value: trackingStatistic?.statFieldValue?.value || 0,
				valueAsText: trackingStatistic?.statFieldValue?.text || '',
			}
		: null;

	const storyPoints =
		!isNil(estimateStatistic) &&
		isNumericFieldEstimate(estimateStatistic) &&
		!isNil(estimateStatistic?.statFieldValue?.value)
			? Number(estimateStatistic?.statFieldValue?.value)
			: null;

	const originalEstimate =
		!isNil(estimateStatistic) &&
		isTimeOriginalEstimate(estimateStatistic) &&
		!isNil(estimateStatistic?.statFieldValue?.value) &&
		!isNil(estimateStatistic?.statFieldValue?.text)
			? {
					value: estimateStatistic?.statFieldValue?.value,
					valueAsText: estimateStatistic?.statFieldValue?.text,
				}
			: null;

	return {
		storyPoints,
		originalEstimate,
		timeRemainingEstimate,
	};
};

const transformPriority = ({ priorityName, priorityUrl }: Issue): CardPriority | null =>
	priorityName && priorityUrl
		? {
				name: priorityName,
				iconUrl: priorityUrl,
			}
		: null;

const transformAssignee = ({ assigneeAccountId, assigneeName, avatarUrl }: Issue): Person | null =>
	assigneeAccountId && assigneeName && avatarUrl
		? {
				id: assigneeAccountId,
				displayName: assigneeName,
				avatarUrl,
			}
		: null;

const transformParentId = (issue: Issue): number | null => {
	if (isSubtaskHierarchyLevel(issue.typeHierarchyLevel)) {
		return issue.parentId ?? null;
	}
	if (!hasEpicField(issue) || !isEpicField(issue.epicField)) {
		return null;
	}
	return issue.epicField.issueId ?? null;
};

const toExtraField = (extraField: IssueExtraField): ExtraField | undefined => {
	const { id, label, renderer, text, html } = extraField;
	if (isExtraFieldHtml(extraField)) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return { id, label, renderer, html } as ExtraFieldHtml;
	}
	if (isExtraFieldDate(extraField)) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return { id, label, renderer, text, html } as ExtraFieldDate;
	}
	if (isExtraFieldText(extraField)) {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return { id, label, renderer, text } as ExtraFieldText;
	}
	return undefined;
};

const transformCardExtraFields = (issue: Issue): ExtraField[] =>
	(issue?.extraFields ?? []).map(toExtraField).filter(Boolean);

const getIssueEpicColor = (issue: Issue) =>
	hasEpicField(issue) ? issue.epicField.color || issue.epicField.epicColor : issue.color;

export const transformCard = (issue: Issue): Card => ({
	id: issue.id,
	key: issue.key,
	summary: issue.summary,
	labels: issue.labels ?? [],
	assignee: transformAssignee(issue),
	typeId: Number(issue.typeId),
	icon: issue.typeUrl,
	isDone: issue.done,
	isFlagged: issue.flagged,
	issueUrl: `/browse/${issue.key}`,
	statusId: issue.statusId,
	parentId: transformParentId(issue),
	parentKey: issue.parentKey,
	priority: transformPriority(issue),
	projectId: issue.projectId,
	dueDate:
		issue.dueDate &&
		expVal('jsw_cmp_show_due_date_by_default_experiment', 'isDueDateFeatureEnabled', false)
			? issue.dueDate
			: null,
	estimate: transformEstimateStatistic(issue),
	numCompleteChildren: 0, // TODO: UIF not available
	numTotalChildren: 0, // TODO: UIF not available
	fixVersions: issue.fixVersions?.map(String),
	color: issue.color,
	epicColor: getIssueEpicColor(issue),
	extraFields: transformCardExtraFields(issue),
	typeHierarchyLevel: issue.typeHierarchyLevel,
	hidden: issue.hidden,
	issueTypeName: issue.typeName,
});
